import {searchUrl} from '@github-ui/issue-url-helper'
import {IssueTypePicker, type IssueTypePickerIssueType} from '@github-ui/item-picker/IssueTypePicker'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {IssueOpenedIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import {LABELS} from '../../../constants/labels'
import {TEST_IDS} from '../../../constants/test-ids'
import {useQueryContext, useQueryEditContext} from '../../../contexts/QueryContext'
import type {AppPayload} from '../../../types/app-payload'
import {getTokensByType, replaceTokensDifferentially} from '../../../utils/urls'
import styles from './ListIssueTypeFilter.module.css'
import type {FilterBarPickerProps} from './ListItemsHeaderWithoutBulkActions'

export function ListIssueTypeFilter({repo: scopedRepo, applySectionFilter, nested}: FilterBarPickerProps) {
  const {name: repo, owner} = scopedRepo
  const {activeSearchQuery, currentViewId} = useQueryContext()
  const {debouncedDirtySearchQuery} = useQueryEditContext()
  const {current_user_settings} = useAppPayload<AppPayload>()

  const query = debouncedDirtySearchQuery ?? activeSearchQuery

  const NoIssueTypeItem = useMemo(() => getNoIssueTypeItem(query), [query])

  const onSelectionChanged = useCallback(
    (issueTypes: IssueTypePickerIssueType[] | null) => {
      const newTokens =
        issueTypes?.map(issueType => {
          if (issueType.id === SPECIAL_VALUES.noTypeData.id) {
            return 'no:type'
          }
          return issueType.name
        }) || []

      const newQuery = replaceTokensDifferentially(query, newTokens, 'type')
      const url = searchUrl({viewId: currentViewId, query: newQuery})
      applySectionFilter(newQuery, url)
    },
    [query, applySectionFilter, currentViewId],
  )

  // Currently we just show the last type to support single selects, which is the same limitation in other pickers
  const currentTypeToken = useMemo(() => getTokensByType(query, 'type').at(-1), [query])

  return (
    <IssueTypePicker
      title={LABELS.filters.issueTypeLabel}
      repo={repo}
      owner={owner}
      readonly={false}
      activeIssueType={null}
      issueTypeToken={currentTypeToken}
      onSelectionChange={onSelectionChanged}
      shortcutEnabled={current_user_settings?.use_single_key_shortcut || false}
      anchorElement={nested ? NestedIssueTypeAnchor : IssueTypeAnchor}
      width="medium"
      noTypeItem={NoIssueTypeItem}
    />
  )
}

function NestedIssueTypeAnchor(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <ActionList.Item {...props} aria-label={LABELS.filters.issueTypeLabel} role="menuitem">
      <ActionList.LeadingVisual>
        <IssueOpenedIcon />
      </ActionList.LeadingVisual>
      {LABELS.filters.issueType}...
    </ActionList.Item>
  )
}

function IssueTypeAnchor(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      variant="invisible"
      data-testid={TEST_IDS.issueTypeAnchorFilter}
      trailingVisual={TriangleDownIcon}
      aria-label={LABELS.filters.issueTypeLabel}
      className={styles.issueTypeFilterButton}
      {...props}
    >
      {LABELS.filters.issueType}
    </Button>
  )
}

const getNoIssueTypeItem = (query: string): ExtendedItemProps<IssueTypePickerIssueType> => ({
  id: SPECIAL_VALUES.noTypeData.id,
  description: '',
  descriptionVariant: 'inline',
  children: <VerifiedHTMLText html={SPECIAL_VALUES.noTypeData.name as SafeHTMLString} />,
  source: SPECIAL_VALUES.noTypeData as IssueTypePickerIssueType,
  groupId: '',
  selected: query.includes('no:type'),
})

try{ ListIssueTypeFilter.displayName ||= 'ListIssueTypeFilter' } catch {}
try{ NoIssueTypeItem.displayName ||= 'NoIssueTypeItem' } catch {}
try{ NestedIssueTypeAnchor.displayName ||= 'NestedIssueTypeAnchor' } catch {}
try{ IssueTypeAnchor.displayName ||= 'IssueTypeAnchor' } catch {}